/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Y = window, ct = Y.ShadowRoot && (Y.ShadyCSS === void 0 || Y.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, wt = Symbol(), dt = /* @__PURE__ */ new WeakMap();
let bt = class {
  constructor(t, e, s) {
    if (this._$cssResult$ = !0, s !== wt)
      throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = t, this.t = e;
  }
  get styleSheet() {
    let t = this.o;
    const e = this.t;
    if (ct && t === void 0) {
      const s = e !== void 0 && e.length === 1;
      s && (t = dt.get(e)), t === void 0 && ((this.o = t = new CSSStyleSheet()).replaceSync(this.cssText), s && dt.set(e, t));
    }
    return t;
  }
  toString() {
    return this.cssText;
  }
};
const vt = (r) => new bt(typeof r == "string" ? r : r + "", void 0, wt), R = (r, ...t) => {
  const e = r.length === 1 ? r[0] : t.reduce((s, i, o) => s + ((n) => {
    if (n._$cssResult$ === !0)
      return n.cssText;
    if (typeof n == "number")
      return n;
    throw Error("Value passed to 'css' function must be a 'css' function result: " + n + ". Use 'unsafeCSS' to pass non-literal values, but take care to ensure page security.");
  })(i) + r[o + 1], r[0]);
  return new bt(e, r, wt);
}, kt = (r, t) => {
  ct ? r.adoptedStyleSheets = t.map((e) => e instanceof CSSStyleSheet ? e : e.styleSheet) : t.forEach((e) => {
    const s = document.createElement("style"), i = Y.litNonce;
    i !== void 0 && s.setAttribute("nonce", i), s.textContent = e.cssText, r.appendChild(s);
  });
}, pt = ct ? (r) => r : (r) => r instanceof CSSStyleSheet ? ((t) => {
  let e = "";
  for (const s of t.cssRules)
    e += s.cssText;
  return vt(e);
})(r) : r;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
var F;
const P = window, ht = P.trustedTypes, $t = ht ? ht.emptyScript : "", Mt = P.reactiveElementPolyfillSupport, et = { toAttribute(r, t) {
  switch (t) {
    case Boolean:
      r = r ? $t : null;
      break;
    case Object:
    case Array:
      r = r == null ? r : JSON.stringify(r);
  }
  return r;
}, fromAttribute(r, t) {
  let e = r;
  switch (t) {
    case Boolean:
      e = r !== null;
      break;
    case Number:
      e = r === null ? null : Number(r);
      break;
    case Object:
    case Array:
      try {
        e = JSON.parse(r);
      } catch {
        e = null;
      }
  }
  return e;
} }, At = (r, t) => t !== r && (t == t || r == r), K = { attribute: !0, type: String, converter: et, reflect: !1, hasChanged: At };
let f = class extends HTMLElement {
  constructor() {
    super(), this._$Ei = /* @__PURE__ */ new Map(), this.isUpdatePending = !1, this.hasUpdated = !1, this._$El = null, this.u();
  }
  static addInitializer(t) {
    var e;
    this.finalize(), ((e = this.h) !== null && e !== void 0 ? e : this.h = []).push(t);
  }
  static get observedAttributes() {
    this.finalize();
    const t = [];
    return this.elementProperties.forEach((e, s) => {
      const i = this._$Ep(s, e);
      i !== void 0 && (this._$Ev.set(i, s), t.push(i));
    }), t;
  }
  static createProperty(t, e = K) {
    if (e.state && (e.attribute = !1), this.finalize(), this.elementProperties.set(t, e), !e.noAccessor && !this.prototype.hasOwnProperty(t)) {
      const s = typeof t == "symbol" ? Symbol() : "__" + t, i = this.getPropertyDescriptor(t, s, e);
      i !== void 0 && Object.defineProperty(this.prototype, t, i);
    }
  }
  static getPropertyDescriptor(t, e, s) {
    return { get() {
      return this[e];
    }, set(i) {
      const o = this[t];
      this[e] = i, this.requestUpdate(t, o, s);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(t) {
    return this.elementProperties.get(t) || K;
  }
  static finalize() {
    if (this.hasOwnProperty("finalized"))
      return !1;
    this.finalized = !0;
    const t = Object.getPrototypeOf(this);
    if (t.finalize(), t.h !== void 0 && (this.h = [...t.h]), this.elementProperties = new Map(t.elementProperties), this._$Ev = /* @__PURE__ */ new Map(), this.hasOwnProperty("properties")) {
      const e = this.properties, s = [...Object.getOwnPropertyNames(e), ...Object.getOwnPropertySymbols(e)];
      for (const i of s)
        this.createProperty(i, e[i]);
    }
    return this.elementStyles = this.finalizeStyles(this.styles), !0;
  }
  static finalizeStyles(t) {
    const e = [];
    if (Array.isArray(t)) {
      const s = new Set(t.flat(1 / 0).reverse());
      for (const i of s)
        e.unshift(pt(i));
    } else
      t !== void 0 && e.push(pt(t));
    return e;
  }
  static _$Ep(t, e) {
    const s = e.attribute;
    return s === !1 ? void 0 : typeof s == "string" ? s : typeof t == "string" ? t.toLowerCase() : void 0;
  }
  u() {
    var t;
    this._$E_ = new Promise((e) => this.enableUpdating = e), this._$AL = /* @__PURE__ */ new Map(), this._$Eg(), this.requestUpdate(), (t = this.constructor.h) === null || t === void 0 || t.forEach((e) => e(this));
  }
  addController(t) {
    var e, s;
    ((e = this._$ES) !== null && e !== void 0 ? e : this._$ES = []).push(t), this.renderRoot !== void 0 && this.isConnected && ((s = t.hostConnected) === null || s === void 0 || s.call(t));
  }
  removeController(t) {
    var e;
    (e = this._$ES) === null || e === void 0 || e.splice(this._$ES.indexOf(t) >>> 0, 1);
  }
  _$Eg() {
    this.constructor.elementProperties.forEach((t, e) => {
      this.hasOwnProperty(e) && (this._$Ei.set(e, this[e]), delete this[e]);
    });
  }
  createRenderRoot() {
    var t;
    const e = (t = this.shadowRoot) !== null && t !== void 0 ? t : this.attachShadow(this.constructor.shadowRootOptions);
    return kt(e, this.constructor.elementStyles), e;
  }
  connectedCallback() {
    var t;
    this.renderRoot === void 0 && (this.renderRoot = this.createRenderRoot()), this.enableUpdating(!0), (t = this._$ES) === null || t === void 0 || t.forEach((e) => {
      var s;
      return (s = e.hostConnected) === null || s === void 0 ? void 0 : s.call(e);
    });
  }
  enableUpdating(t) {
  }
  disconnectedCallback() {
    var t;
    (t = this._$ES) === null || t === void 0 || t.forEach((e) => {
      var s;
      return (s = e.hostDisconnected) === null || s === void 0 ? void 0 : s.call(e);
    });
  }
  attributeChangedCallback(t, e, s) {
    this._$AK(t, s);
  }
  _$EO(t, e, s = K) {
    var i;
    const o = this.constructor._$Ep(t, s);
    if (o !== void 0 && s.reflect === !0) {
      const n = (((i = s.converter) === null || i === void 0 ? void 0 : i.toAttribute) !== void 0 ? s.converter : et).toAttribute(e, s.type);
      this._$El = t, n == null ? this.removeAttribute(o) : this.setAttribute(o, n), this._$El = null;
    }
  }
  _$AK(t, e) {
    var s;
    const i = this.constructor, o = i._$Ev.get(t);
    if (o !== void 0 && this._$El !== o) {
      const n = i.getPropertyOptions(o), w = typeof n.converter == "function" ? { fromAttribute: n.converter } : ((s = n.converter) === null || s === void 0 ? void 0 : s.fromAttribute) !== void 0 ? n.converter : et;
      this._$El = o, this[o] = w.fromAttribute(e, n.type), this._$El = null;
    }
  }
  requestUpdate(t, e, s) {
    let i = !0;
    t !== void 0 && (((s = s || this.constructor.getPropertyOptions(t)).hasChanged || At)(this[t], e) ? (this._$AL.has(t) || this._$AL.set(t, e), s.reflect === !0 && this._$El !== t && (this._$EC === void 0 && (this._$EC = /* @__PURE__ */ new Map()), this._$EC.set(t, s))) : i = !1), !this.isUpdatePending && i && (this._$E_ = this._$Ej());
  }
  async _$Ej() {
    this.isUpdatePending = !0;
    try {
      await this._$E_;
    } catch (e) {
      Promise.reject(e);
    }
    const t = this.scheduleUpdate();
    return t != null && await t, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    var t;
    if (!this.isUpdatePending)
      return;
    this.hasUpdated, this._$Ei && (this._$Ei.forEach((i, o) => this[o] = i), this._$Ei = void 0);
    let e = !1;
    const s = this._$AL;
    try {
      e = this.shouldUpdate(s), e ? (this.willUpdate(s), (t = this._$ES) === null || t === void 0 || t.forEach((i) => {
        var o;
        return (o = i.hostUpdate) === null || o === void 0 ? void 0 : o.call(i);
      }), this.update(s)) : this._$Ek();
    } catch (i) {
      throw e = !1, this._$Ek(), i;
    }
    e && this._$AE(s);
  }
  willUpdate(t) {
  }
  _$AE(t) {
    var e;
    (e = this._$ES) === null || e === void 0 || e.forEach((s) => {
      var i;
      return (i = s.hostUpdated) === null || i === void 0 ? void 0 : i.call(s);
    }), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(t)), this.updated(t);
  }
  _$Ek() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$E_;
  }
  shouldUpdate(t) {
    return !0;
  }
  update(t) {
    this._$EC !== void 0 && (this._$EC.forEach((e, s) => this._$EO(s, this[s], e)), this._$EC = void 0), this._$Ek();
  }
  updated(t) {
  }
  firstUpdated(t) {
  }
};
f.finalized = !0, f.elementProperties = /* @__PURE__ */ new Map(), f.elementStyles = [], f.shadowRootOptions = { mode: "open" }, Mt == null || Mt({ ReactiveElement: f }), ((F = P.reactiveElementVersions) !== null && F !== void 0 ? F : P.reactiveElementVersions = []).push("1.6.1");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
var X;
const Q = window, v = Q.trustedTypes, Lt = v ? v.createPolicy("lit-html", { createHTML: (r) => r }) : void 0, j = `lit$${(Math.random() + "").slice(9)}$`, Dt = "?" + j, Ot = `<${Dt}>`, A = document, T = (r = "") => A.createComment(r), z = (r) => r === null || typeof r != "object" && typeof r != "function", St = Array.isArray, _t = (r) => St(r) || typeof (r == null ? void 0 : r[Symbol.iterator]) == "function", E = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, gt = /-->/g, xt = />/g, y = RegExp(`>|[ 	
\f\r](?:([^\\s"'>=/]+)([ 	
\f\r]*=[ 	
\f\r]*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), mt = /'/g, jt = /"/g, Nt = /^(?:script|style|textarea|title)$/i, Yt = (r) => (t, ...e) => ({ _$litType$: r, strings: t, values: e }), h = Yt(1), C = Symbol.for("lit-noChange"), c = Symbol.for("lit-nothing"), yt = /* @__PURE__ */ new WeakMap(), b = A.createTreeWalker(A, 129, null, !1), Pt = (r, t) => {
  const e = r.length - 1, s = [];
  let i, o = t === 2 ? "<svg>" : "", n = E;
  for (let a = 0; a < e; a++) {
    const l = r[a];
    let m, u, d = -1, x = 0;
    for (; x < l.length && (n.lastIndex = x, u = n.exec(l), u !== null); )
      x = n.lastIndex, n === E ? u[1] === "!--" ? n = gt : u[1] !== void 0 ? n = xt : u[2] !== void 0 ? (Nt.test(u[2]) && (i = RegExp("</" + u[2], "g")), n = y) : u[3] !== void 0 && (n = y) : n === y ? u[0] === ">" ? (n = i ?? E, d = -1) : u[1] === void 0 ? d = -2 : (d = n.lastIndex - u[2].length, m = u[1], n = u[3] === void 0 ? y : u[3] === '"' ? jt : mt) : n === jt || n === mt ? n = y : n === gt || n === xt ? n = E : (n = y, i = void 0);
    const O = n === y && r[a + 1].startsWith("/>") ? " " : "";
    o += n === E ? l + Ot : d >= 0 ? (s.push(m), l.slice(0, d) + "$lit$" + l.slice(d) + j + O) : l + j + (d === -2 ? (s.push(void 0), a) : O);
  }
  const w = o + (r[e] || "<?>") + (t === 2 ? "</svg>" : "");
  if (!Array.isArray(r) || !r.hasOwnProperty("raw"))
    throw Error("invalid template strings array");
  return [Lt !== void 0 ? Lt.createHTML(w) : w, s];
};
class k {
  constructor({ strings: t, _$litType$: e }, s) {
    let i;
    this.parts = [];
    let o = 0, n = 0;
    const w = t.length - 1, a = this.parts, [l, m] = Pt(t, e);
    if (this.el = k.createElement(l, s), b.currentNode = this.el.content, e === 2) {
      const u = this.el.content, d = u.firstChild;
      d.remove(), u.append(...d.childNodes);
    }
    for (; (i = b.nextNode()) !== null && a.length < w; ) {
      if (i.nodeType === 1) {
        if (i.hasAttributes()) {
          const u = [];
          for (const d of i.getAttributeNames())
            if (d.endsWith("$lit$") || d.startsWith(j)) {
              const x = m[n++];
              if (u.push(d), x !== void 0) {
                const O = i.getAttribute(x.toLowerCase() + "$lit$").split(j), _ = /([.?@])?(.*)/.exec(x);
                a.push({ type: 1, index: o, name: _[2], strings: O, ctor: _[1] === "." ? Ut : _[1] === "?" ? Gt : _[1] === "@" ? Rt : H });
              } else
                a.push({ type: 6, index: o });
            }
          for (const d of u)
            i.removeAttribute(d);
        }
        if (Nt.test(i.tagName)) {
          const u = i.textContent.split(j), d = u.length - 1;
          if (d > 0) {
            i.textContent = v ? v.emptyScript : "";
            for (let x = 0; x < d; x++)
              i.append(u[x], T()), b.nextNode(), a.push({ type: 2, index: ++o });
            i.append(u[d], T());
          }
        }
      } else if (i.nodeType === 8)
        if (i.data === Dt)
          a.push({ type: 2, index: o });
        else {
          let u = -1;
          for (; (u = i.data.indexOf(j, u + 1)) !== -1; )
            a.push({ type: 7, index: o }), u += j.length - 1;
        }
      o++;
    }
  }
  static createElement(t, e) {
    const s = A.createElement("template");
    return s.innerHTML = t, s;
  }
}
function D(r, t, e = r, s) {
  var i, o, n, w;
  if (t === C)
    return t;
  let a = s !== void 0 ? (i = e._$Co) === null || i === void 0 ? void 0 : i[s] : e._$Cl;
  const l = z(t) ? void 0 : t._$litDirective$;
  return (a == null ? void 0 : a.constructor) !== l && ((o = a == null ? void 0 : a._$AO) === null || o === void 0 || o.call(a, !1), l === void 0 ? a = void 0 : (a = new l(r), a._$AT(r, e, s)), s !== void 0 ? ((n = (w = e)._$Co) !== null && n !== void 0 ? n : w._$Co = [])[s] = a : e._$Cl = a), a !== void 0 && (t = D(r, a._$AS(r, t.values), a, s)), t;
}
class Qt {
  constructor(t, e) {
    this.u = [], this._$AN = void 0, this._$AD = t, this._$AM = e;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  v(t) {
    var e;
    const { el: { content: s }, parts: i } = this._$AD, o = ((e = t == null ? void 0 : t.creationScope) !== null && e !== void 0 ? e : A).importNode(s, !0);
    b.currentNode = o;
    let n = b.nextNode(), w = 0, a = 0, l = i[0];
    for (; l !== void 0; ) {
      if (w === l.index) {
        let m;
        l.type === 2 ? m = new $(n, n.nextSibling, this, t) : l.type === 1 ? m = new l.ctor(n, l.name, l.strings, this, t) : l.type === 6 && (m = new Ht(n, this, t)), this.u.push(m), l = i[++a];
      }
      w !== (l == null ? void 0 : l.index) && (n = b.nextNode(), w++);
    }
    return o;
  }
  p(t) {
    let e = 0;
    for (const s of this.u)
      s !== void 0 && (s.strings !== void 0 ? (s._$AI(t, s, e), e += s.strings.length - 2) : s._$AI(t[e])), e++;
  }
}
class $ {
  constructor(t, e, s, i) {
    var o;
    this.type = 2, this._$AH = c, this._$AN = void 0, this._$AA = t, this._$AB = e, this._$AM = s, this.options = i, this._$Cm = (o = i == null ? void 0 : i.isConnected) === null || o === void 0 || o;
  }
  get _$AU() {
    var t, e;
    return (e = (t = this._$AM) === null || t === void 0 ? void 0 : t._$AU) !== null && e !== void 0 ? e : this._$Cm;
  }
  get parentNode() {
    let t = this._$AA.parentNode;
    const e = this._$AM;
    return e !== void 0 && t.nodeType === 11 && (t = e.parentNode), t;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(t, e = this) {
    t = D(this, t, e), z(t) ? t === c || t == null || t === "" ? (this._$AH !== c && this._$AR(), this._$AH = c) : t !== this._$AH && t !== C && this.g(t) : t._$litType$ !== void 0 ? this.$(t) : t.nodeType !== void 0 ? this.T(t) : _t(t) ? this.k(t) : this.g(t);
  }
  O(t, e = this._$AB) {
    return this._$AA.parentNode.insertBefore(t, e);
  }
  T(t) {
    this._$AH !== t && (this._$AR(), this._$AH = this.O(t));
  }
  g(t) {
    this._$AH !== c && z(this._$AH) ? this._$AA.nextSibling.data = t : this.T(A.createTextNode(t)), this._$AH = t;
  }
  $(t) {
    var e;
    const { values: s, _$litType$: i } = t, o = typeof i == "number" ? this._$AC(t) : (i.el === void 0 && (i.el = k.createElement(i.h, this.options)), i);
    if (((e = this._$AH) === null || e === void 0 ? void 0 : e._$AD) === o)
      this._$AH.p(s);
    else {
      const n = new Qt(o, this), w = n.v(this.options);
      n.p(s), this.T(w), this._$AH = n;
    }
  }
  _$AC(t) {
    let e = yt.get(t.strings);
    return e === void 0 && yt.set(t.strings, e = new k(t)), e;
  }
  k(t) {
    St(this._$AH) || (this._$AH = [], this._$AR());
    const e = this._$AH;
    let s, i = 0;
    for (const o of t)
      i === e.length ? e.push(s = new $(this.O(T()), this.O(T()), this, this.options)) : s = e[i], s._$AI(o), i++;
    i < e.length && (this._$AR(s && s._$AB.nextSibling, i), e.length = i);
  }
  _$AR(t = this._$AA.nextSibling, e) {
    var s;
    for ((s = this._$AP) === null || s === void 0 || s.call(this, !1, !0, e); t && t !== this._$AB; ) {
      const i = t.nextSibling;
      t.remove(), t = i;
    }
  }
  setConnected(t) {
    var e;
    this._$AM === void 0 && (this._$Cm = t, (e = this._$AP) === null || e === void 0 || e.call(this, t));
  }
}
class H {
  constructor(t, e, s, i, o) {
    this.type = 1, this._$AH = c, this._$AN = void 0, this.element = t, this.name = e, this._$AM = i, this.options = o, s.length > 2 || s[0] !== "" || s[1] !== "" ? (this._$AH = Array(s.length - 1).fill(new String()), this.strings = s) : this._$AH = c;
  }
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(t, e = this, s, i) {
    const o = this.strings;
    let n = !1;
    if (o === void 0)
      t = D(this, t, e, 0), n = !z(t) || t !== this._$AH && t !== C, n && (this._$AH = t);
    else {
      const w = t;
      let a, l;
      for (t = o[0], a = 0; a < o.length - 1; a++)
        l = D(this, w[s + a], e, a), l === C && (l = this._$AH[a]), n || (n = !z(l) || l !== this._$AH[a]), l === c ? t = c : t !== c && (t += (l ?? "") + o[a + 1]), this._$AH[a] = l;
    }
    n && !i && this.j(t);
  }
  j(t) {
    t === c ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, t ?? "");
  }
}
class Ut extends H {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(t) {
    this.element[this.name] = t === c ? void 0 : t;
  }
}
const Jt = v ? v.emptyScript : "";
class Gt extends H {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(t) {
    t && t !== c ? this.element.setAttribute(this.name, Jt) : this.element.removeAttribute(this.name);
  }
}
class Rt extends H {
  constructor(t, e, s, i, o) {
    super(t, e, s, i, o), this.type = 5;
  }
  _$AI(t, e = this) {
    var s;
    if ((t = (s = D(this, t, e, 0)) !== null && s !== void 0 ? s : c) === C)
      return;
    const i = this._$AH, o = t === c && i !== c || t.capture !== i.capture || t.once !== i.once || t.passive !== i.passive, n = t !== c && (i === c || o);
    o && this.element.removeEventListener(this.name, this, i), n && this.element.addEventListener(this.name, this, t), this._$AH = t;
  }
  handleEvent(t) {
    var e, s;
    typeof this._$AH == "function" ? this._$AH.call((s = (e = this.options) === null || e === void 0 ? void 0 : e.host) !== null && s !== void 0 ? s : this.element, t) : this._$AH.handleEvent(t);
  }
}
class Ht {
  constructor(t, e, s) {
    this.element = t, this.type = 6, this._$AN = void 0, this._$AM = e, this.options = s;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(t) {
    D(this, t);
  }
}
const Ct = Q.litHtmlPolyfillSupport;
Ct == null || Ct(k, $), ((X = Q.litHtmlVersions) !== null && X !== void 0 ? X : Q.litHtmlVersions = []).push("2.6.1");
const Wt = (r, t, e) => {
  var s, i;
  const o = (s = e == null ? void 0 : e.renderBefore) !== null && s !== void 0 ? s : t;
  let n = o._$litPart$;
  if (n === void 0) {
    const w = (i = e == null ? void 0 : e.renderBefore) !== null && i !== void 0 ? i : null;
    o._$litPart$ = n = new $(t.insertBefore(T(), w), w, void 0, e ?? {});
  }
  return n._$AI(r), n;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
var V, q;
class p extends f {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    var t, e;
    const s = super.createRenderRoot();
    return (t = (e = this.renderOptions).renderBefore) !== null && t !== void 0 || (e.renderBefore = s.firstChild), s;
  }
  update(t) {
    const e = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(t), this._$Do = Wt(e, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    var t;
    super.connectedCallback(), (t = this._$Do) === null || t === void 0 || t.setConnected(!0);
  }
  disconnectedCallback() {
    var t;
    super.disconnectedCallback(), (t = this._$Do) === null || t === void 0 || t.setConnected(!1);
  }
  render() {
    return C;
  }
}
p.finalized = !0, p._$litElement$ = !0, (V = globalThis.litElementHydrateSupport) === null || V === void 0 || V.call(globalThis, { LitElement: p });
const ft = globalThis.litElementPolyfillSupport;
ft == null || ft({ LitElement: p });
((q = globalThis.litElementVersions) !== null && q !== void 0 ? q : globalThis.litElementVersions = []).push("3.2.2");
const Bt = `:root{--color-purple-10: #f8ecfc;--color-purple-20: #ebccf5;--color-purple-40: #d0a0e0;--color-purple-60: #9b64ae;--color-purple-80: #7a3093;--color-purple-90: #641c7d;--color-purple-100: #45005c;--color-purple-accent: #ff3b9c;--color-blue-10: #f4f3ff;--color-blue-20: #d4d1ed;--color-blue-40: #b2afd7;--color-blue-60: #7c79ad;--color-blue-80: #4c4698;--color-blue-90: #3c3882;--color-blue-100: #262262;--color-blue-accent: #1d9cd9;--color-green-10: #e5f3f5;--color-green-20: #bce1e4;--color-green-40: #99cbcf;--color-green-60: #639fa4;--color-green-80: #2f7f86;--color-green-90: #1c6e75;--color-green-100: #00555c;--color-green-accent: #b3e347;--color-gray-10: #f2f2f4;--color-gray-20: #dde0e3;--color-gray-40: #a5abb4;--color-gray-60: #79818f;--color-gray-80: #4c576a;--color-gray-90: #3a465a;--color-gray-100: #202e45;--color-gray-accent: #a7c3b9;--color-global-base-white: #fff;--color-global-base-black: #000;--color-global-accent-yellow: #fedd00;--color-global-accent-white: #fff;--color-global-accent-green: #008a00;--color-global-accent-green-darken: #026702;--color-global-link-blue: #212aa1;--color-global-link-darken: #1857b2;--color-global-link-decoration: #aaa6d3;--color-global-warning-red: #e01445;--color-global-warning-darken: #f91c51;--color-cerise: #d63284;--color-denim: #177db0;--color-box-shadow-black: #000029}:root,.theme-blue,[data-theme=blue]{--bg-surface: var(--color-blue-100);--bg-surface-faded: rgba(var(--color-blue-100), .85);--bg-surface-medium: var(--color-blue-90);--bg-primary: var(--color-blue-10);--bg-primary-darken: var(--color-blue-40);--bg-splash: var(--color-green-20);--bg-splash-darken: var(--color-green-40);--others-on-bg-surface: var(--color-blue-80);--others-on-bg-primary: var(--color-blue-100);--others-accent: var(--color-blue-accent);--others-lighten: var(--color-blue-20);--others-darken: var(--color-blue-60)}.theme-gray,[data-theme=gray]{--bg-surface: var(--color-gray-100);--bg-surface-faded: rgba(var(--color-gray-100), .85);--bg-surface-medium: var(--color-gray-90);--bg-primary: var(--color-gray-10);--bg-primary-darken: var(--color-gray-40);--bg-splash: var(--color-blue-20);--bg-splash-darken: var(--color-blue-40);--others-on-bg-surface: var(--color-gray-80);--others-on-bg-primary: var(--color-gray-100);--others-accent: var(--color-gray-accent);--others-lighten: var(--color-gray-20);--others-darken: var(--color-gray-60)}.theme-green,[data-theme=green]{--bg-surface: var(--color-green-100);--bg-surface-faded: rgba(var(--color-green-100), .85);--bg-surface-medium: var(--color-green-90);--bg-primary: var(--color-green-10);--bg-primary-darken: var(--color-green-40);--bg-splash: var(--color-purple-20);--bg-splash-darken: var(--color-purple-40);--others-on-bg-surface: var(--color-green-80);--others-on-bg-primary: var(--color-green-100);--others-accent: var(--color-green-accent);--others-lighten: var(--color-green-20);--others-darken: var(--color-green-60)}.theme-purple,[data-theme=purple]{--bg-surface: var(--color-purple-100);--bg-surface-faded: rgba(var(--color-purple-100), .85);--bg-surface-medium: var(--color-purple-90);--bg-primary: var(--color-purple-10);--bg-primary-darken: var(--color-purple-40);--bg-splash: var(--color-green-20);--bg-splash-darken: var(--color-green-40);--others-on-bg-surface: var(--color-purple-80);--others-on-bg-primary: var(--color-purple-100);--others-accent: var(--color-purple-accent);--others-lighten: var(--color-purple-20);--others-darken: var(--color-purple-60)}:root{--generic-bg-white: var(--color-global-base-white);--generic-bg-shadow: var(--color-box-shadow-black);--generic-bg-gray: var(--color-gray-10);--generic-bg-ark: var(--color-global-base-white);--generic-bg-mapbutton: var(--color-gray-80);--generic-bg-disabled: var(--color-gray-40);--generic-bg-disabled-darken: var(--color-gray-60);--generic-bg-service: var(--color-global-accent-green);--generic-bg-service-darken: var(--color-global-accent-green-darken);--generic-bg-error: var(--color-global-warning-red);--generic-bg-error-darken: var(--color-global-warning-darken);--generic-text-default: var(--color-gray-100);--generic-text-white: var(--color-global-base-white);--generic-text-strong: var(--color-global-base-black);--generic-text-faded: var(--color-gray-80);--generic-text-link: var(--color-global-link-blue);--generic-text-link-darken: var(--color-global-link-darken);--generic-text-link-decoration: var(--color-global-link-decoration);--generic-text-link-visited: var(--color-global-link-visited);--generic-text-link-visited-border: var(--color-global-link-visited-border);--generic-text-on-bg-default: var(--color-blue-100);--generic-text-on-bg-faded: rgba(var(--color-blue-100), .95);--generic-text-on-surface: var(--color-global-accent-white);--generic-text-plain-label: var(--color-gray-10);--generic-text-plain: var(--color-gray-20);--generic-text-plain-darken: var(--color-gray-40);--generic-others-primary-color: var(--color-blue-100);--generic-others-theme-accent: var(--color-global-accent-white);--generic-others-brand-accent: var(--color-global-accent-yellow);--fontsize-xxxs: 16px;--fontsize-xxs: 18px;--fontsize-xs: 20px;--fontsize-sm: 22px;--fontsize-md: 24px;--fontsize-lg: 28px;--fontsize-xl: 36px;--fontsize-xxl: 48px;--fontsize-xxxl: 60px;--fontsize-xxxxl: 80px;--space-4xs: 2px;--space-3xs: 4px;--space-2xs: 8px;--space-xs: 12px;--space-sm: 16px;--space-md: 20px;--space-lg: 24px;--space-xl: 32px;--space-2xl: 40px;--space-3xl: 48px;--space-4xl: 56px;--layout-space-2xs: 16px;--layout-space-xs: 24px;--layout-space-sm: 32px;--layout-space-md: 48px;--layout-space-lg: 64px;--layout-space-xl: 80px;--layout-space-2xl: 96px}*,:before,:after{box-sizing:border-box;border-width:0;border-style:solid;border-color:#e5e7eb}:before,:after{--tw-content: ""}html{line-height:1.5;-webkit-text-size-adjust:100%;-moz-tab-size:4;-o-tab-size:4;tab-size:4;font-family:ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica Neue,Arial,Noto Sans,sans-serif,"Apple Color Emoji","Segoe UI Emoji",Segoe UI Symbol,"Noto Color Emoji";font-feature-settings:normal;font-variation-settings:normal}body{margin:0;line-height:inherit}hr{height:0;color:inherit;border-top-width:1px}abbr:where([title]){-webkit-text-decoration:underline dotted;text-decoration:underline dotted}h1,h2,h3,h4,h5,h6{font-size:inherit;font-weight:inherit}a{color:inherit;text-decoration:inherit}b,strong{font-weight:bolder}code,kbd,samp,pre{font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,Liberation Mono,Courier New,monospace;font-size:1em}small{font-size:80%}sub,sup{font-size:75%;line-height:0;position:relative;vertical-align:baseline}sub{bottom:-.25em}sup{top:-.5em}table{text-indent:0;border-color:inherit;border-collapse:collapse}button,input,optgroup,select,textarea{font-family:inherit;font-size:100%;font-weight:inherit;line-height:inherit;color:inherit;margin:0;padding:0}button,select{text-transform:none}button,[type=button],[type=reset],[type=submit]{-webkit-appearance:button;background-color:transparent;background-image:none}:-moz-focusring{outline:auto}:-moz-ui-invalid{box-shadow:none}progress{vertical-align:baseline}::-webkit-inner-spin-button,::-webkit-outer-spin-button{height:auto}[type=search]{-webkit-appearance:textfield;outline-offset:-2px}::-webkit-search-decoration{-webkit-appearance:none}::-webkit-file-upload-button{-webkit-appearance:button;font:inherit}summary{display:list-item}blockquote,dl,dd,h1,h2,h3,h4,h5,h6,hr,figure,p,pre{margin:0}fieldset{margin:0;padding:0}legend{padding:0}ol,ul,menu{list-style:none;margin:0;padding:0}textarea{resize:vertical}input::-moz-placeholder,textarea::-moz-placeholder{opacity:1;color:#9ca3af}input::placeholder,textarea::placeholder{opacity:1;color:#9ca3af}button,[role=button]{cursor:pointer}:disabled{cursor:default}img,svg,video,canvas,audio,iframe,embed,object{display:block;vertical-align:middle}img,video{max-width:100%;height:auto}[hidden]{display:none}*,:before,:after{--tw-border-spacing-x: 0;--tw-border-spacing-y: 0;--tw-translate-x: 0;--tw-translate-y: 0;--tw-rotate: 0;--tw-skew-x: 0;--tw-skew-y: 0;--tw-scale-x: 1;--tw-scale-y: 1;--tw-pan-x: ;--tw-pan-y: ;--tw-pinch-zoom: ;--tw-scroll-snap-strictness: proximity;--tw-gradient-from-position: ;--tw-gradient-via-position: ;--tw-gradient-to-position: ;--tw-ordinal: ;--tw-slashed-zero: ;--tw-numeric-figure: ;--tw-numeric-spacing: ;--tw-numeric-fraction: ;--tw-ring-inset: ;--tw-ring-offset-width: 0px;--tw-ring-offset-color: #fff;--tw-ring-color: rgb(59 130 246 / .5);--tw-ring-offset-shadow: 0 0 #0000;--tw-ring-shadow: 0 0 #0000;--tw-shadow: 0 0 #0000;--tw-shadow-colored: 0 0 #0000;--tw-blur: ;--tw-brightness: ;--tw-contrast: ;--tw-grayscale: ;--tw-hue-rotate: ;--tw-invert: ;--tw-saturate: ;--tw-sepia: ;--tw-drop-shadow: ;--tw-backdrop-blur: ;--tw-backdrop-brightness: ;--tw-backdrop-contrast: ;--tw-backdrop-grayscale: ;--tw-backdrop-hue-rotate: ;--tw-backdrop-invert: ;--tw-backdrop-opacity: ;--tw-backdrop-saturate: ;--tw-backdrop-sepia: }::backdrop{--tw-border-spacing-x: 0;--tw-border-spacing-y: 0;--tw-translate-x: 0;--tw-translate-y: 0;--tw-rotate: 0;--tw-skew-x: 0;--tw-skew-y: 0;--tw-scale-x: 1;--tw-scale-y: 1;--tw-pan-x: ;--tw-pan-y: ;--tw-pinch-zoom: ;--tw-scroll-snap-strictness: proximity;--tw-gradient-from-position: ;--tw-gradient-via-position: ;--tw-gradient-to-position: ;--tw-ordinal: ;--tw-slashed-zero: ;--tw-numeric-figure: ;--tw-numeric-spacing: ;--tw-numeric-fraction: ;--tw-ring-inset: ;--tw-ring-offset-width: 0px;--tw-ring-offset-color: #fff;--tw-ring-color: rgb(59 130 246 / .5);--tw-ring-offset-shadow: 0 0 #0000;--tw-ring-shadow: 0 0 #0000;--tw-shadow: 0 0 #0000;--tw-shadow-colored: 0 0 #0000;--tw-blur: ;--tw-brightness: ;--tw-contrast: ;--tw-grayscale: ;--tw-hue-rotate: ;--tw-invert: ;--tw-saturate: ;--tw-sepia: ;--tw-drop-shadow: ;--tw-backdrop-blur: ;--tw-backdrop-brightness: ;--tw-backdrop-contrast: ;--tw-backdrop-grayscale: ;--tw-backdrop-hue-rotate: ;--tw-backdrop-invert: ;--tw-backdrop-opacity: ;--tw-backdrop-saturate: ;--tw-backdrop-sepia: }.container{width:100%;margin-right:auto;margin-left:auto}@media (min-width: 540px){.container{max-width:540px}}@media (min-width: 720px){.container{max-width:720px}}@media (min-width: 960px){.container{max-width:960px}}@media (min-width: 1140px){.container{max-width:1140px}}@media (min-width: 1320px){.container{max-width:1320px}}.visible{visibility:visible}.collapse{visibility:collapse}.static{position:static}.absolute{position:absolute}.relative{position:relative}.right-0{right:0px}.top-0{top:0px}.z-\\[300\\]{z-index:300}.z-\\[301\\]{z-index:301}.m-\\[-1px\\]{margin:-1px}.my-auto{margin-top:auto;margin-bottom:auto}.mb-12{margin-bottom:3rem}.mb-4{margin-bottom:1rem}.mb-4xl{margin-bottom:56px}.mb-\\[12px\\]{margin-bottom:12px}.mb-\\[6px\\]{margin-bottom:6px}.ml-0{margin-left:0}.ml-4xl{margin-left:56px}.mr-5{margin-right:1.25rem}.mt-layout-sm{margin-top:32px}.block{display:block}.inline{display:inline}.flex{display:flex}.table{display:table}.list-item{display:list-item}.hidden{display:none}.h-0{height:0px}.h-0\\.5{height:.125rem}.h-12{height:3rem}.h-20{height:5rem}.h-\\[1px\\]{height:1px}.h-\\[24px\\]{height:24px}.h-\\[78px\\]{height:78px}.h-full{height:100%}.w-\\[1px\\]{width:1px}.w-\\[34px\\]{width:34px}.w-\\[78px\\]{width:78px}.w-fit{width:-moz-fit-content;width:fit-content}.w-full{width:100%}.flex-grow{flex-grow:1}.border-collapse{border-collapse:collapse}.origin-bottom-right{transform-origin:bottom right}.origin-center{transform-origin:center}.origin-top-right{transform-origin:top right}.transform{transform:translate(var(--tw-translate-x),var(--tw-translate-y)) rotate(var(--tw-rotate)) skew(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))}.cursor-pointer{cursor:pointer}.flex-col{flex-direction:column}.flex-col-reverse{flex-direction:column-reverse}.items-center{align-items:center}.justify-start{justify-content:flex-start}.justify-end{justify-content:flex-end}.justify-between{justify-content:space-between}.overflow-hidden{overflow:hidden}.border{border-width:1px}.border-none{border-style:none}.bg-current{background-color:currentColor}.bg-lighten{background-color:var(--others-lighten)}.bg-surface{background-color:var(--bg-surface)}.bg-white{background-color:var(--generic-bg-white)}.pb-4{padding-bottom:1rem}.pb-4xl{padding-bottom:56px}.pt-4{padding-top:1rem}.pt-8{padding-top:2rem}.text-left{text-align:left}.text-center{text-align:center}.text-heading-md{font-size:24px;line-height:28px;letter-spacing:-.25px;font-weight:600}.text-lg{font-size:1.125rem;line-height:1.75rem}.text-par-md{font-size:18px;line-height:24px;letter-spacing:0px;font-weight:400}.text-par-sm{font-size:16px;line-height:20px;font-weight:400}.text-sm{font-size:.875rem;line-height:1.25rem}.font-bold{font-weight:700}.font-normal{font-weight:400}.font-semibold{font-weight:600}.text-brand-accent{color:var(--generic-others-brand-accent)}.text-lighten{color:var(--others-lighten)}.text-white{color:var(--generic-text-white)}.underline{text-decoration-line:underline}.shadow{--tw-shadow: 0 1px 3px 0 rgb(0 0 0 / .1), 0 1px 2px -1px rgb(0 0 0 / .1);--tw-shadow-colored: 0 1px 3px 0 var(--tw-shadow-color), 0 1px 2px -1px var(--tw-shadow-color);box-shadow:var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)}.shadow-link{--tw-shadow: 0 2px currentColor;--tw-shadow-colored: 0 2px var(--tw-shadow-color);box-shadow:var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)}.shadow-linksmall{--tw-shadow: 0 1px currentColor;--tw-shadow-colored: 0 1px var(--tw-shadow-color);box-shadow:var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)}.shadow-none{--tw-shadow: 0 0 #0000;--tw-shadow-colored: 0 0 #0000;box-shadow:var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)}.outline{outline-style:solid}.transition{transition-property:color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,-webkit-backdrop-filter;transition-property:color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter;transition-property:color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter,-webkit-backdrop-filter;transition-timing-function:cubic-bezier(.4,0,.2,1);transition-duration:.15s}.duration-300{transition-duration:.3s}.will-change-\\[transform\\,opacity\\]{will-change:transform,opacity}.will-change-\\[visibility\\,box-shadow\\]{will-change:visibility,box-shadow}:host{font-family:Source Sans Pro,sans-serif,arial;color:var(--generic-text-default);font-size:var(--fontsize-xxs);line-height:24px;font-weight:400;letter-spacing:0}input,button,select,textarea{font-family:inherit}@media (min-width: 768px){.md\\:container{width:100%;margin-right:auto;margin-left:auto}@media (min-width: 540px){.md\\:container{max-width:540px}}@media (min-width: 720px){.md\\:container{max-width:720px}}@media (min-width: 960px){.md\\:container{max-width:960px}}@media (min-width: 1140px){.md\\:container{max-width:1140px}}@media (min-width: 1320px){.md\\:container{max-width:1320px}}}.hover\\:bg-darken:hover{background-color:var(--others-darken)}.hover\\:text-white:hover{color:var(--generic-text-white)}.hover\\:shadow-link:hover{--tw-shadow: 0 2px currentColor;--tw-shadow-colored: 0 2px var(--tw-shadow-color);box-shadow:var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)}@media (min-width: 768px){.md\\:z-\\[301\\]{z-index:301}.md\\:mb-0{margin-bottom:0}.md\\:mb-12{margin-bottom:3rem}.md\\:ml-auto{margin-left:auto}.md\\:block{display:block}.md\\:hidden{display:none}.md\\:h-\\[20px\\]{height:20px}.md\\:h-layout-2xl{height:96px}.md\\:min-h-\\[28px\\]{min-height:28px}.md\\:w-\\[160px\\]{width:160px}.md\\:w-\\[40vw\\]{width:40vw}.md\\:w-full{width:100%}.md\\:min-w-\\[500px\\]{min-width:500px}.md\\:flex-grow{flex-grow:1}.md\\:flex-row{flex-direction:row}.md\\:justify-between{justify-content:space-between}.md\\:border-b-\\[1px\\]{border-bottom-width:1px}.md\\:p-layout-sm{padding:32px}.md\\:pl-0{padding-left:0}.md\\:text-left{text-align:left}.md\\:text-default{color:var(--generic-text-default)}}
`, M = vt(Bt);
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const L = (r) => (t) => typeof t == "function" ? ((e, s) => (customElements.define(e, s), s))(r, t) : ((e, s) => {
  const { kind: i, elements: o } = s;
  return { kind: i, elements: o, finisher(n) {
    customElements.define(e, n);
  } };
})(r, t);
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Zt = (r, t) => t.kind === "method" && t.descriptor && !("value" in t.descriptor) ? { ...t, finisher(e) {
  e.createProperty(t.key, r);
} } : { kind: "field", key: Symbol(), placement: "own", descriptor: {}, originalKey: t.key, initializer() {
  typeof t.initializer == "function" && (this[t.key] = t.initializer.call(this));
}, finisher(e) {
  e.createProperty(t.key, r);
} };
function g(r) {
  return (t, e) => e !== void 0 ? ((s, i, o) => {
    i.constructor.createProperty(o, s);
  })(r, t, e) : Zt(r, t);
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function It(r) {
  return g({ ...r, state: !0 });
}
/**
 * @license
 * Copyright 2021 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
var tt;
((tt = window.HTMLSlotElement) === null || tt === void 0 ? void 0 : tt.prototype.assignedElements) != null;
var Ft = Object.defineProperty, Kt = Object.getOwnPropertyDescriptor, Xt = (r, t, e, s) => {
  for (var i = s > 1 ? void 0 : s ? Kt(t, e) : t, o = r.length - 1, n; o >= 0; o--)
    (n = r[o]) && (i = (s ? n(t, e, i) : n(i)) || i);
  return s && i && Ft(t, e, i), i;
};
let it = class extends p {
  render() {
    return h`
            <span class="h-[1px] absolute overflow-hidden w-[1px] m-[-1px] border-none">
                <slot></slot>
            </span>
        `;
  }
};
it.styles = [M];
it = Xt([
  L("ua-visually-hidden")
], it);
var Vt = Object.defineProperty, qt = Object.getOwnPropertyDescriptor, te = (r, t, e, s) => {
  for (var i = s > 1 ? void 0 : s ? qt(t, e) : t, o = r.length - 1, n; o >= 0; o--)
    (n = r[o]) && (i = (s ? n(t, e, i) : n(i)) || i);
  return s && i && Vt(t, e, i), i;
};
let st = class extends p {
  render() {
    return h`
      <footer class="relative bg-surface text-white">
        <div class="footer surface">
          <ua-visually-hidden><h1>Sidfot</h1></ua-visually-hidden>
          <div class="container ml-0 md:ml-auto">
            <slot></slot>
          </div>
        </div>
      </footer>
    `;
  }
};
st.styles = [
  M,
  R`
      .footer {
        padding-top: 64px;
        padding-bottom: 64px;
        padding-left: 24px;
        padding-right: 24px;
      }

      @media screen and (min-width: 992px) {
        .footer {
          padding-top: 96px;
          padding-bottom: 96px;
        }
      }

      @media screen and (min-width: 768px) {
        .footer {
          padding-top: 80px;
          padding-bottom: 80px;
        }
      }
    `
];
st = te([
  L("ua-footer")
], st);
var ee = Object.defineProperty, ie = Object.getOwnPropertyDescriptor, W = (r, t, e, s) => {
  for (var i = s > 1 ? void 0 : s ? ie(t, e) : t, o = r.length - 1, n; o >= 0; o--)
    (n = r[o]) && (i = (s ? n(t, e, i) : n(i)) || i);
  return s && i && ee(t, e, i), i;
};
let S = class extends p {
  constructor() {
    super(...arguments), this.headerId = "", this.title = "", this.placeStart = "top";
  }
  render() {
    return h`
      <div class="text-white mb-12 md:mb-0 h-full">
        ${this.title ? h` <slot name="title">
              <h2
                id="${this.headerId}"
                class="text-heading-md text-white md:min-h-[28px]"
              >
                ${this.title}
              </h2>
            </slot>` : c}
        <div
          class="${this.placeStart === "bottom" ? "flex flex-col justify-end h-full" : c}"
        >
          <slot> </slot>
        </div>
      </div>
    `;
  }
};
S.styles = [M];
W([
  g({ type: String })
], S.prototype, "headerId", 2);
W([
  g({ type: String })
], S.prototype, "title", 2);
W([
  g({ type: String })
], S.prototype, "placeStart", 2);
S = W([
  L("ua-footer-column")
], S);
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const se = { ATTRIBUTE: 1, CHILD: 2, PROPERTY: 3, BOOLEAN_ATTRIBUTE: 4, EVENT: 5, ELEMENT: 6 }, re = (r) => (...t) => ({ _$litDirective$: r, values: t });
class oe {
  constructor(t) {
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AT(t, e, s) {
    this._$Ct = t, this._$AM = e, this._$Ci = s;
  }
  _$AS(t, e) {
    return this.update(t, e);
  }
  update(t, e) {
    return this.render(...e);
  }
}
/**
 * @license
 * Copyright 2018 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const ne = re(class extends oe {
  constructor(r) {
    var t;
    if (super(r), r.type !== se.ATTRIBUTE || r.name !== "class" || ((t = r.strings) === null || t === void 0 ? void 0 : t.length) > 2)
      throw Error("`classMap()` can only be used in the `class` attribute and must be the only part in the attribute.");
  }
  render(r) {
    return " " + Object.keys(r).filter((t) => r[t]).join(" ") + " ";
  }
  update(r, [t]) {
    var e, s;
    if (this.nt === void 0) {
      this.nt = /* @__PURE__ */ new Set(), r.strings !== void 0 && (this.st = new Set(r.strings.join(" ").split(/\s/).filter((o) => o !== "")));
      for (const o in t)
        t[o] && !(!((e = this.st) === null || e === void 0) && e.has(o)) && this.nt.add(o);
      return this.render(t);
    }
    const i = r.element.classList;
    this.nt.forEach((o) => {
      o in t || (i.remove(o), this.nt.delete(o));
    });
    for (const o in t) {
      const n = !!t[o];
      n === this.nt.has(o) || !((s = this.st) === null || s === void 0) && s.has(o) || (n ? (i.add(o), this.nt.add(o)) : (i.remove(o), this.nt.delete(o)));
    }
    return C;
  }
});
var ae = Object.defineProperty, le = Object.getOwnPropertyDescriptor, B = (r, t, e, s) => {
  for (var i = s > 1 ? void 0 : s ? le(t, e) : t, o = r.length - 1, n; o >= 0; o--)
    (n = r[o]) && (i = (s ? n(t, e, i) : n(i)) || i);
  return s && i && ae(t, e, i), i;
};
let N = class extends p {
  constructor() {
    super(...arguments), this.href = "#", this.small = "false", this.type = "text";
  }
  getClasses() {
    return {
      "text-par-sm": this.small === "true",
      "text-par-md": this.small === "false",
      "shadow-linksmall": this.small === "true" && this.type === "text",
      "shadow-link": this.small === "false" && this.type === "text"
    };
  }
  render() {
    return h`
            <a href=${this.href} class="text-lighten hover:text-white ${ne(this.getClasses())}">
                <slot></slot>
            </a>
        `;
  }
};
N.styles = [M];
B([
  g({ type: String })
], N.prototype, "href", 2);
B([
  g({ type: String })
], N.prototype, "small", 2);
B([
  g({ type: String })
], N.prototype, "type", 2);
N = B([
  L("ua-link")
], N);
var ue = Object.defineProperty, ce = Object.getOwnPropertyDescriptor, we = (r, t, e, s) => {
  for (var i = s > 1 ? void 0 : s ? ce(t, e) : t, o = r.length - 1, n; o >= 0; o--)
    (n = r[o]) && (i = (s ? n(t, e, i) : n(i)) || i);
  return s && i && ue(t, e, i), i;
};
let rt = class extends p {
  render() {
    return h`
      <ua-footer>
        <div class="layout-threecolumn layout-threecolumnnarrow text-left md:text-default">
          <ua-footer-column title="Kontakt">
            <ul class="pt-4 pb-4">
              <li class="mb-4 mr-5 text-white text-par-md">
                <span itemprop="contactType"
                  >Kontaktcenter:<ua-link
                    class="phone-link"
                    href="tel:018-7270000"
                    ><span itemprop="telephone">018-727 00 00</span></ua-link
                  ></span
                >
              </li>
              <li class="mb-4 mr-5 text-white text-par-md">
                <span itemprop="contactType">
                  E-post:
                  <ua-link href="mailto:uppsala.kommun@uppsala.se">
                    <span itemprop="email">uppsala.kommun@uppsala.se</span>
                  </ua-link>
                </span>
              </li>
              <li class="mb-4 mr-5 text-white text-par-md">
                <ua-link
                  href="https://www.uppsala.se/lankar-i-sidfoten/kontakta-uppsala-kommun/"
                  >Fler kontaktvägar</ua-link
                >
              </li>
              <li class="pt-8 mb-4 mr-5 text-white text-par-md">
                <ua-link
                  href="https://www.uppsala.se/lankar-i-sidfoten/press/"
                  type="text"
                >
                  Pressrum
                </ua-link>
              </li>
              <li class="mb-4 mr-5 text-white text-par-md">
                <ua-link
                  href="https://www.uppsala.se/kommun-och-politik/nyheter-och-pressmeddelanden/?query=&datedelimiter=&isbeforedate=0&typeid=98&sortorder=1&organisationid=&subcategoryid=&page="
                >
                  Nyheter och pressmeddelanden
                </ua-link>
              </li>
            </ul>
          </ua-footer-column>

          <ua-footer-column title="Om webbplatsen">
            <ul class="pt-4 pb-4">
              <li class="mb-4 mr-5 text-white text-par-md">
                <ua-link
                  href="https://www.uppsala.se/lankar-i-sidfoten/om-webbplatsen/"
                >
                  Om webbplatsen
                </ua-link>
              </li>
              <li class="mb-4 mr-5 text-white text-par-md">
                <ua-link
                  href="https://www.uppsala.se/kommun-och-politik/arkiv-och-allmanna-handlingar/allmanna-handlingar-och-diarium/"
                >
                  Allmänna handlingar och diarium
                </ua-link>
              </li>
              <li class="mb-4 mr-5 text-white text-par-md">
                <ua-link
                  href="https://www.uppsala.se/kommun-och-politik/sa-fungerar-kommunen/behandling-av-personuppgifter/"
                >
                  Behandling av personuppgifter
                </ua-link>
              </li>
              <li class="mb-4 mr-5 text-white text-par-md">
                <ua-link
                  href="https://www.uppsala.se/lankar-i-sidfoten/om-webbplatsen/kakor/"
                >
                  Kakor
                </ua-link>
              </li>
              <li class="mb-4 mr-5 text-white text-par-md">
                <ua-link href="https://www.uppsala.se/sprak/sprak/">
                  Språk (other languages)
                </ua-link>
              </li>
              <li class="mb-4 mr-5 text-white text-par-md">
                <ua-link
                  href="https://www.uppsala.se/lankar-i-sidfoten/tillganglighetsredogorelse/"
                >
                  Tillgänglighetsredogörelse
                </ua-link>
              </li>
            </ul>
          </ua-footer-column>

          <ua-footer-column placeStart="bottom">
            <ul class="flex md:mb-12">
              <li class="mr-5">
                <ua-link
                  type="icon"
                  href="http://www.facebook.com/uppsalakommun"
                  ><span
                    ><img
                      style="height:30px;width:30px;"
                      src="https://www.uppsala.se/Content/Images/Svg/facebook_logo_white.svg"
                      alt="Facebook" /></span
                ></ua-link>
              </li>
              <li class="mr-5">
                <ua-link
                  type="icon"
                  href="https://se.linkedin.com/company/uppsala-kommun"
                  ><span
                    ><img
                      style="height:30px;width:30px;"
                      src="https://www.uppsala.se/Content/Images/Svg/linkedin_logo_white.svg"
                      alt="Linkedin" /></span
                ></ua-link>
              </li>
              <li class="mr-5">
                <ua-link
                  type="icon"
                  href="https://www.uppsala.se/lankar-i-sidfoten/om-webbplatsen/rss/"
                  ><span
                    ><img
                      style="height:30px;width:30px;"
                      src="https://www.uppsala.se/Content/Images/Svg/rss.svg"
                      alt="RSS" /></span
                ></ua-link>
              </li>
              <li class="mr-5">
                <ua-link
                  href="https://www.uppsala.se/lankar-i-sidfoten/flera-satt-att-folja-oss/"
                  ><span>Fler sätt att följa oss</span></ua-link
                >
              </li>
            </ul>
          </ua-footer-column>
        </div>
      </ua-footer>
    `;
  }
};
rt.styles = [
  M,
  R`
      @media screen and (min-width: 992px) {
        .footer--socialmedia {
          position: absolute;
          bottom: 0;
        }
      }

      @media screen and (min-width: 992px) {
        .layout-threecolumn {
          display: flex;
          flex-flow: row wrap;
        }
      }

      @media screen and (min-width: 768px) {
        .layout-threecolumnnarrow {
          display: flex;
          flex-flow: row wrap;
        }
      }

      .layout-threecolumn > :first-child {
        margin-top: 0;
      }

      @media screen and (min-width: 992px) {
        .layout-threecolumn > *:nth-child(3n + 1) {
          margin-left: 0;
        }

        .layout-threecolumn > * {
          width: calc(33.33% - 21.3333333333px);
          margin-left: 32px;
        }
      }
    `
];
rt = we([
  L("ua-footer-pre-populated")
], rt);
var de = Object.defineProperty, pe = Object.getOwnPropertyDescriptor, he = (r, t, e, s) => {
  for (var i = s > 1 ? void 0 : s ? pe(t, e) : t, o = r.length - 1, n; o >= 0; o--)
    (n = r[o]) && (i = (s ? n(t, e, i) : n(i)) || i);
  return s && i && de(t, e, i), i;
};
let ot = class extends p {
  render() {
    return h`
            <ul class="pt-4 pb-4">
                <slot></slot>
            <ul>
        `;
  }
};
ot.styles = [M];
ot = he([
  L("ua-link-list")
], ot);
var Me = Object.defineProperty, Le = Object.getOwnPropertyDescriptor, ge = (r, t, e, s) => {
  for (var i = s > 1 ? void 0 : s ? Le(t, e) : t, o = r.length - 1, n; o >= 0; o--)
    (n = r[o]) && (i = (s ? n(t, e, i) : n(i)) || i);
  return s && i && Me(t, e, i), i;
};
let nt = class extends p {
  render() {
    return h`
            <li class="mb-4 mr-5 text-white text-par-md">
                <slot>
                </slot>
            </li>
        `;
  }
};
nt.styles = [M];
nt = ge([
  L("ua-link-list-item")
], nt);
var xe = Object.defineProperty, me = Object.getOwnPropertyDescriptor, je = (r, t, e, s) => {
  for (var i = s > 1 ? void 0 : s ? me(t, e) : t, o = r.length - 1, n; o >= 0; o--)
    (n = r[o]) && (i = (s ? n(t, e, i) : n(i)) || i);
  return s && i && xe(t, e, i), i;
};
let at = class extends p {
  render() {
    return h`
            <ul class="flex md:mb-12">
                <slot>
                </slot>
            </ul>
        `;
  }
};
at.styles = [M];
at = je([
  L("ua-link-list-row")
], at);
var ye = Object.defineProperty, Ce = Object.getOwnPropertyDescriptor, fe = (r, t, e, s) => {
  for (var i = s > 1 ? void 0 : s ? Ce(t, e) : t, o = r.length - 1, n; o >= 0; o--)
    (n = r[o]) && (i = (s ? n(t, e, i) : n(i)) || i);
  return s && i && ye(t, e, i), i;
};
let lt = class extends p {
  render() {
    return h`
            <li class="mr-5"><slot></slot></li>
        `;
  }
};
lt.styles = [M];
lt = fe([
  L("ua-link-list-row-item")
], lt);
const be = "data:image/svg+xml;base64,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", ve = "data:image/svg+xml;base64,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";
var Ae = Object.defineProperty, De = Object.getOwnPropertyDescriptor, Et = (r, t, e, s) => {
  for (var i = s > 1 ? void 0 : s ? De(t, e) : t, o = r.length - 1, n; o >= 0; o--)
    (n = r[o]) && (i = (s ? n(t, e, i) : n(i)) || i);
  return s && i && Ae(t, e, i), i;
};
let U = class extends p {
  constructor() {
    super(...arguments), this.isOpen = !1;
  }
  toggleMenu() {
    this.isOpen = !this.isOpen, this.dispatchEvent(new CustomEvent(
      "on-menubutton-click",
      {
        detail: {
          isOpen: this.isOpen
        }
      }
    ));
  }
  render() {
    let r = `block h-0.5 w-full mb-[6px] transform transition duration-300 ${this.isOpen ? "bg-white" : "bg-current"}`;
    return h`
                <div role="application" 
                    aria-label=${this.isOpen ? "Stäng meny" : "Öppna meny"}
                    aria-expanded=${this.isOpen}
                    aria-pressed=${this.isOpen}
                    class="bg-lighten hover:bg-darken text-primary-default w-[78px] h-full md:w-[160px]"
                >
                    <button class="flex flex-col-reverse items-center justify-start md:flex-row h-full w-full pb-4 pt-4  md:p-layout-sm" @click=${this.toggleMenu}>
                        <span class=${`md:flex-grow md:z-[301] md:text-left text-par-md ${this.isOpen && "text-white"}`}>
                            ${this.isOpen ? "Stäng" : "Meny"}
                        </span>
                        <span class=${"block w-[34px] h-[24px] md:h-[20px] z-[301]"}>
                            <span class=${r + " origin-top-right"} style=${this.isOpen ? "transform: matrix(.71,-.71,.71,.71,-5,0)" : null}></span>
                            <span class=${r + " origin-center"} style=${this.isOpen ? "transform: matrix(.71,.71,-.71,.71,0,4)" : null}></span>
                            <span class=${r + " origin-bottom-right"} style=${this.isOpen ? "transform: matrix(.71,.71,-.71,.71,-5,8)" : null}></span>
                        </span>
                    </button>
                </div>
            `;
  }
};
U.styles = [M];
Et([
  g({ type: Boolean, attribute: !1 }),
  It()
], U.prototype, "isOpen", 2);
U = Et([
  L("ua-menu-button")
], U);
var Se = Object.defineProperty, Ne = Object.getOwnPropertyDescriptor, Tt = (r, t, e, s) => {
  for (var i = s > 1 ? void 0 : s ? Ne(t, e) : t, o = r.length - 1, n; o >= 0; o--)
    (n = r[o]) && (i = (s ? n(t, e, i) : n(i)) || i);
  return s && i && Se(t, e, i), i;
};
let J = class extends p {
  constructor() {
    super(...arguments), this.isOpen = !1;
  }
  setShowNavBar(r) {
    this.isOpen = r;
  }
  render() {
    return h`
            <ua-menu-button aria-controls="ua-header-menu-main" @on-menubutton-click="${(r) => {
      this.setShowNavBar(r.detail.isOpen);
    }}" isOpen=${this.isOpen ?? c}></ua-menu-button>
            <section id="ua-header-menu-main" aria-label="Meny" class=${"menu z-[300] absolute top-0 right-0 collapse w-full md:w-[40vw] md:min-w-[500px] overflow-hidden shadow-none will-change-[visibility,box-shadow] " + (this.isOpen ? "isopen" : "")} role="application" aria-label="Länkmeny">
                <div class="menu-content bg-surface pb-4xl text-white shadow-none will-change-[transform,opacity]">
                    <div class="h-[78px] md:h-layout-2xl md:border-b-[1px] border-divider-color-on-surface">
                        <!-- Spacer -->
                    </div>
                    <nav class="ml-4xl pb-4xl mt-layout-sm ">
                        <slot></slot>
                    </nav>
                </div>
            </section>
            `;
  }
};
J.styles = [M, R`
        .menu {
            transition: visibility .5s step-end;
        }
        .menu.isopen {
            visibility: visible;
            transition: visibility 0s, box-shadow .3s step-end;
            box-shadow: 0 0 15px rgba(0,0,0,.3);
        }
        .menu-content {
            opacity: 0;
            transform: translateX(100%);
            transition: step-end,transform .4s ease-in,opacity .4s;
            will-change: transform,opacity;
            box-shadow: 0 0 15px rgba(0,0,0,.3);
        }
        .menu.isopen .menu-content {
            opacity: 1;
            transform: translateX(0);
            transition: transform .3s ease-out,opacity .2s;
        }

    `];
Tt([
  g({ type: Boolean }),
  It()
], J.prototype, "isOpen", 2);
J = Tt([
  L("ua-menu")
], J);
var Ie = Object.defineProperty, Ee = Object.getOwnPropertyDescriptor, Te = (r, t, e, s) => {
  for (var i = s > 1 ? void 0 : s ? Ee(t, e) : t, o = r.length - 1, n; o >= 0; o--)
    (n = r[o]) && (i = (s ? n(t, e, i) : n(i)) || i);
  return s && i && Ie(t, e, i), i;
};
let ut = class extends p {
  render() {
    return h`<div class="mb-4xl"></div>`;
  }
};
ut.styles = [M];
ut = Te([
  L("ua-menu-spacer")
], ut);
const ze = {
  xs: 480,
  sm: 768,
  md: 992,
  lg: 1200,
  xl: 1400
};
var ke = Object.defineProperty, $e = Object.getOwnPropertyDescriptor, zt = (r, t, e, s) => {
  for (var i = s > 1 ? void 0 : s ? $e(t, e) : t, o = r.length - 1, n; o >= 0; o--)
    (n = r[o]) && (i = (s ? n(t, e, i) : n(i)) || i);
  return s && i && ke(t, e, i), i;
};
let G = class extends p {
  constructor() {
    super(...arguments), this.title = "";
  }
  render() {
    return h`
      <header class="header bg-surface relative">
        <div class="md:container flex h-20 md:h-layout-2xl justify-between">
          <!-- Left side -->
          <div class="left-side-continer md:w-full md:pl-0 flex h-full">
            <!-- Desktop logo -->
            <div class="logo-container flip-container my-auto hidden md:block">
              <a
                href="#main"
                class="flip-back text-sm text-center text-brand-accent"
              >
                Till sidan huvudinnehåll
              </a>
              <a href="/" class="flip-front ">
                <img
                  src=${be}
                  class="h-12 mt-space-3xs"
                  alt="logga av uppsala kommun med ett gult lejon"
                />
              </a>
            </div>

            <!-- Mobile logo -->
            <div class="logo-container md:hidden my-auto">
              <a href="/" class="">
                <img
                  src=${ve}
                  class="h-12 mt-space-3xs"
                  alt="logga av uppsala kommun med ett gult lejon"
                />
              </a>
            </div>

            <!-- Displayed title -->
            ${this.title.length > 0 && h`<div class="title-container">
              <h1 class="title-text">${this.title}</h1>
            </div>`}
          </div>
          <div class="flex md:justify-between justify-end md:flex-grow">
            <div class="flex-grow relative">
              <slot name="search"> </slot>
            </div>

            <!-- Left -->
            <slot></slot>
          </div>
        </div>
      </header>
    `;
  }
};
G.styles = [
  M,
  R`
      .header {
        padding-left: 24px;
        padding-right: 24px;
      }

      .flip-container {
        perspective: 1000px;
        position: relative;
      }

      .flip-back {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        transform: rotateX(-180deg);
        z-index: -9;
      }

      .flip-back,
      .flip-front {
        display: flex;
        align-items: center;
        backface-visibility: hidden;
        transition:
          transform 0.6s,
          z-index 0.6s;
        transform-style: preserve-3d;
        will-change: transform;
      }

      .flip-back:focus {
        transform: rotateX(0deg);
        z-index: 9;
      }

      .flip-back:focus + .flip-front {
        transform: rotateX(180deg);
      }

      .logo-container {
        padding-left: 0px;
        padding-right: 54px;
      }

      .title-container {
        height: 100%;
        background-color: #4c4698;
        padding-left: 36px;
        padding-right: 36px;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .title-text {
        font-size: 24px;
        letter-spacing: 0;
        font-weight: 400;
        line-height: 28px;
        color: #fff;
        margin: 0;
      }

      @media (max-width: ${ze.md}px) {
        .left-side-continer {
          width: 100%;
        }

        .logo-container {
          padding-left: 0px;
          padding-right: 20px;
        }
        .title-container {
          width: 100%;
          padding-left: 5%;
          padding-right: 5%;
        }

        .title-text {
          font-size: 18px;
        }

        .header {
          padding-right: 0px;
        }
      }
    `
];
zt([
  g({ type: String })
], G.prototype, "title", 2);
G = zt([
  L("ua-header")
], G);
var Oe = Object.defineProperty, _e = Object.getOwnPropertyDescriptor, Z = (r, t, e, s) => {
  for (var i = s > 1 ? void 0 : s ? _e(t, e) : t, o = r.length - 1, n; o >= 0; o--)
    (n = r[o]) && (i = (s ? n(t, e, i) : n(i)) || i);
  return s && i && Oe(t, e, i), i;
};
let I = class extends p {
  constructor() {
    super(...arguments), this.size = "default", this.href = null, this.target = null;
  }
  getButtonSizeClass() {
    return this.size !== "small" ? "text-heading-md font-semibold hover:shadow-link hover:text-white" : "text-par-sm font-normal shadow-linksmall hover:text-white";
  }
  render() {
    return h`
            <a class=${`block w-fit text-lighten cursor-pointer mb-[12px] ${this.getButtonSizeClass()}`} href=${this.href ?? c} target=${this.target ?? c}><slot></slot></a>
            `;
  }
};
I.styles = [M];
Z([
  g({ type: String })
], I.prototype, "size", 2);
Z([
  g({ type: String })
], I.prototype, "href", 2);
Z([
  g({ type: String })
], I.prototype, "target", 2);
I = Z([
  L("ua-menu-link")
], I);
export {
  st as Footer,
  S as FooterColumn,
  it as FooterLink,
  rt as FooterPrePopulated,
  N as Link,
  ot as LinkList,
  nt as LinkListItem,
  at as LinkRow,
  lt as LinkRowItem,
  G as UaHeader,
  U as UaMenuButton,
  I as UaMenuLink,
  ut as UaMenuSpacer,
  J as uaMenu
};
